import { degreeToRadian } from "common/utils/math";
import type { Vec2 } from "geom/vec2";
import { vec2Len, vec2Normalize, vec2Rotate } from "geom/vec2";
import { jsxToString } from "lib/house/annotation/conversion";
import type { BaseAnnoProps, BaseStyleProps } from "lib/house/annotation/svgSettings";
import { baseAnnoDefault, baseStyleDefault } from "lib/house/annotation/svgSettings";

interface LoadLoadConnectionOnlyProps extends BaseAnnoProps {
  load1Position: Vec2;
  load2Position: Vec2;
  flipGuideVectorRotation?: boolean;
}

type LoadLoadConnectionProps = LoadLoadConnectionOnlyProps &
  Pick<
    BaseStyleProps,
    | "fillLightPrimary"
    | "strokeDarkPrimary"
    | "extraSmallStrokeWidth"
    | "ultraSmallStrokeWidth"
    | "textFillPrimary"
    | "regularFontSize"
    | "fontFamily0"
  >;

const loadLoadConnectionDefault: LoadLoadConnectionProps = {
  ...baseStyleDefault,
  ...baseAnnoDefault,
  load1Position: [0, 0],
  load2Position: [100, 100],
};

export function generateLoadLoadConnection(props?: LoadLoadConnectionProps, wrapSvg = false): string {
  return jsxToString(<LoadLoadConnection {...loadLoadConnectionDefault} {...props} />, wrapSvg);
}

const LoadLoadConnection = (props: LoadLoadConnectionProps) => {
  const curveRotation = props.flipGuideVectorRotation ? -30 : 30;
  const [sX, sY] = props.load1Position;
  const [eX, eY] = props.load2Position;

  const [seVecX, seVecY] = [sX - eX, sY - eY];
  const [senVecX, senVecY] = vec2Normalize(seVecX, seVecY);

  const [esVecX, esVecY] = [eX - sX, eY - sY];
  const [esnVecX, esnVecY] = vec2Normalize(esVecX, esVecY);

  const dLength = vec2Len(esVecX, esVecY);

  let [scX, scY] = vec2Rotate((esnVecX * dLength) / 3, (esnVecY * dLength) / 3, degreeToRadian(curveRotation));
  scX = scX + sX;
  scY = scY + sY;
  let [ecX, ecY] = vec2Rotate((senVecX * dLength) / 3, (senVecY * dLength) / 3, -degreeToRadian(curveRotation));
  ecX = ecX + eX;
  ecY = ecY + eY;

  const scale = props.scale;

  return (
    <g transform={`translate(${props.x} ,${props.y}) scale(${scale},${scale})`}>
      <path
        d={`M ${sX} ${sY} C ${scX} ${scY}, ${ecX} ${ecY}, ${eX} ${eY}`}
        stroke={props.strokeDarkPrimary}
        strokeWidth={props.ultraSmallStrokeWidth}
        fill="none"
        strokeDasharray="5"
      />
    </g>
  );
};
